import React, { useState } from 'react';
import styled from 'styled-components';
import { Link as GatsbyLink } from 'gatsby';
import { Box, Flex, Link } from 'rebass/styled-components';
import { HiOutlineMenu, HiOutlineX } from 'react-icons/hi';

const NavbarContainer = styled.div`
  padding: 10px 0px;
  position: fixed;
  top: 0;
  z-index: 100;
  background-color: transparent;
  width: 100%;
`;

const StyledHeader = styled.header`
  border-radius: 16px;
  max-width: 1260px;
  min-height: 72px;
  padding-inline: 20px;
  backdrop-filter: blur(4px);
  background: #ffffffe6;
  justify-content: space-between;
  align-items: center;
  margin-inline: auto;
  display: flex;
  box-shadow: 0 4px 12px #0000001a;
  position: relative; /* Anchor for mobile menu */
  z-index: 101;
`;

const StyledLogo = styled.img`
  height: 40px;
  width: auto;
  display: block;
`;

const NavLink = styled(Link)`
  text-decoration: none;
  font-size: 16px;
  font-weight: 500;
  color: #1a232e;
  transition: opacity 0.2s;
  font-family: 'Sk-Modernist-Regular', sans-serif;
  &:hover {
    opacity: 0.7;
  }
`;

const VerticalDivider = styled.span`
  height: 24px;
  width: 1px;
  background-color: #e0e0e0;
  margin: 0 20px;
`;

const LoginLink = styled(NavLink)`
  color: #ff4f00;
  font-weight: 700;
`;

// New: Mobile specific dropdown container
const MobileMenu = styled(Box)<{ isOpen: boolean }>`
  position: absolute;
  top: 80px; /* Positioned below the header */
  left: 0;
  right: 0;
  background: white;
  border-radius: 16px;
  box-shadow: 0 8px 24px #0000001a;
  padding: 20px;
  display: ${(props) => (props.isOpen ? 'flex' : 'none')};
  flex-direction: column;
  gap: 15px;
  z-index: 100;
  margin: 0 20px;
`;

const MobileNavLink = styled(NavLink)`
  padding: 10px 0;
  border-bottom: 1px solid #f0f0f0;
  width: 100%;
  &:last-child {
    border-bottom: none;
  }
`;

const MenuButton = styled.div`
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #1a232e;

  svg {
    width: 28px;
    height: 28px;
  }
`;

const links = [
  { href: '/', title: 'Home' },
  { href: 'https://www.healthya.co.uk/who-we-are', title: 'About', external: true },
  { href: '/pharmacies', title: 'Pharmacies' },
  { href: '/help', title: 'Help' },
  { href: `${process.env.GATSBY_WEB_APP_URL}/#/login`, title: 'Log in', external: true, isLogin: true },
];

const StandardHeader = () => (
  <StyledHeader>
    <Box>
      <GatsbyLink to="/">
        <StyledLogo src="/images/logo.png" alt="MyPharmacy by healthya" />
      </GatsbyLink>
    </Box>
    <Flex alignItems="center">
      {links.map((link, i) => (
        <React.Fragment key={link.title}>
          {link.isLogin && <VerticalDivider />}
          {link.external ? (
            <NavLink as="a" href={link.href} sx={{ color: link.isLogin ? '#ff5c35' : '#1a232e' }}>
              {link.isLogin ? <LoginLink>{link.title}</LoginLink> : link.title}
            </NavLink>
          ) : (
            <GatsbyLink to={link.href} style={{ textDecoration: 'none' }}>
              <NavLink as="span">{link.title}</NavLink>
            </GatsbyLink>
          )}
          {!link.isLogin && i < links.length - 2 && <Box width={30} />}
          {i === 3 && links.length > 1 && <Box width={30} />}
        </React.Fragment>
      ))}
    </Flex>
  </StyledHeader>
);

const CollapsedHeader = () => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <Box>
      <StyledHeader>
        <StyledLogo src="/images/logo.png" alt="Logo" style={{ height: '35px' }} />
        <MenuButton onClick={() => setIsOpen(!isOpen)}>{isOpen ? <HiOutlineX /> : <HiOutlineMenu />}</MenuButton>
      </StyledHeader>

      <MobileMenu isOpen={isOpen}>
        {links.map((link) => (
          <React.Fragment key={link.title}>
            {link.external ? (
              <MobileNavLink
                as="a"
                href={link.href}
                onClick={() => setIsOpen(false)}
                sx={{ color: link.isLogin ? '#ff5c35' : '#1a232e' }}
              >
                {link.title}
              </MobileNavLink>
            ) : (
              <GatsbyLink
                to={link.href}
                style={{ textDecoration: 'none', width: '100%' }}
                onClick={() => setIsOpen(false)}
              >
                <MobileNavLink as="span">{link.title}</MobileNavLink>
              </GatsbyLink>
            )}
          </React.Fragment>
        ))}
      </MobileMenu>
    </Box>
  );
};

const Header = () => (
  <NavbarContainer>
    <Box sx={{ display: ['none', 'none', 'block'] }} style={{ padding: '0px 20px' }}>
      <StandardHeader />
    </Box>
    <Box sx={{ display: ['block', 'block', 'none'] }} style={{ margin: '0px 20px' }}>
      <CollapsedHeader />
    </Box>
  </NavbarContainer>
);

export default Header;
